import { Link } from "react-router-dom";
import {
  Target,
  PieChart,
  Search,
  Repeat,
  Wallet,
  ClipboardCheck,
  Scale,
  ArrowUpRight,
} from "lucide-react";
import PageHero from "../../components/shared/PageHero";
import CtaBand from "../../components/shared/CtaBand";
import "./MutualFundsPage.css";

const SECTIONS = [
  {
    id: "goal-based",
    icon: Target,
    title: "Goal-Based Planning",
    desc: "Map SIPs and lumpsums to education, retirement, home, or business goals — with clear timelines and inflation-adjusted targets.",
  },
  {
    id: "asset-allocation",
    icon: PieChart,
    title: "Asset Allocation",
    desc: "Equity, debt, hybrid, and gold allocation tailored to your risk profile and time horizon — not a one-size portfolio.",
  },
  {
    id: "fund-selection",
    icon: Search,
    title: "Fund Selection",
    desc: "Research-backed shortlisting across AMCs using consistency, risk-adjusted returns, expenses, and portfolio quality.",
  },
  {
    id: "sip",
    icon: Repeat,
    title: "Systematic Investment Plans (SIP)",
    desc: "Disciplined monthly investing to average costs and build wealth through compounding — start small, stay consistent.",
  },
  {
    id: "lumpsum",
    icon: Wallet,
    title: "Lumpsum Investing",
    desc: "Deploy surplus capital thoughtfully — via staggered entries or goal-aligned funds when market conditions and cash flow allow.",
  },
  {
    id: "portfolio-review",
    icon: ClipboardCheck,
    title: "Portfolio Review",
    desc: "Periodic reviews of fund performance, overlap, and goal progress so underperformers don't silently drag returns.",
  },
  {
    id: "rebalancing",
    icon: Scale,
    title: "Rebalancing",
    desc: "Restore target allocation as markets move — protecting gains and maintaining the risk level you agreed to.",
  },
];

export default function MutualFundsPage() {
  return (
    <div className="mf-page">
      <PageHero
        badge="Mutual Funds"
        title="Goal-based mutual fund wealth management"
        subtitle="SIPs, fund selection, rebalancing, and continuous review — AMFI-aware guidance to grow wealth with discipline."
        actions={[
          { label: "Book Free Consultation", to: "/contact", primary: true },
          { label: "Start SIP", to: "/calculators#sip" },
        ]}
      />

      <section className="pm-section mf-sections">
        <div className="pm-container">
          <span className="pm-badge">
            <span className="pm-badge-dot" />
            Our Approach
          </span>
          <h2 className="pm-section-title">How we manage mutual fund wealth</h2>
          <p className="pm-section-sub">
            From planning to rebalancing — a complete process for Indian investors.
          </p>

          <div className="mf-grid">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              return (
                <article key={s.id} id={s.id} className="pm-card mf-card">
                  <div className="mf-card__icon">
                    <Icon size={22} />
                  </div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </article>
              );
            })}
          </div>

          <div className="mf-cta-row">
            <Link to="/contact" className="pm-btn pm-btn-primary">
              Book Free Consultation
              <ArrowUpRight size={16} />
            </Link>
            <Link to="/calculators#sip" className="pm-btn pm-btn-outline">
              Start SIP
            </Link>
          </div>
        </div>
      </section>

      <CtaBand
        title="Ready to start or optimise your SIPs?"
        subtitle="Get a free consultation or jump into our SIP calculator to plan your first investment."
        primary={{ label: "Book Free Consultation", to: "/contact" }}
        secondary={{ label: "Start SIP", to: "/calculators#sip" }}
      />
    </div>
  );
}
